import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { useLocationCtx } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import OfferCard from "@/components/OfferCard";
import ClientAuthDialog from "@/components/ClientAuthDialog";
import { Search, SlidersHorizontal, Store, ShoppingBag, X, Loader2, Map as MapIcon, UserPlus, BadgeCheck, BellPlus, Users, Rocket, ChevronDown, MapPin } from "lucide-react";
import InviteSheet from "@/components/InviteSheet";
import InviteStoreSearchDialog from "@/components/InviteStoreSearchDialog";
import InstallAppBanner from "@/components/InstallAppBanner";
import ProfileSuggestionBanner from "@/components/ProfileSuggestionBanner";
import SavingsHero from "@/components/SavingsHero";
import AlertMeSheet from "@/components/AlertMeSheet";
import { distanceLabel } from "@/lib/format";
import { CatIcon } from "@/lib/catIcons";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SITE_URL = typeof window !== "undefined" ? window.location.origin : "";
const shareWA = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");

const DISCOUNTS = [
  { v: 0, label: "Tutti" },
  { v: 10, label: "10%+" },
  { v: 20, label: "20%+" },
  { v: 30, label: "30%+" },
  { v: 50, label: "50%+" },
];

export default function Home() {
  const { loc } = useLocationCtx();
  const { openLocation } = useOutletContext() || {};
  const { client, refreshClient } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [selCats, setSelCats] = useState(() => {
    const c = searchParams.get("cat");
    return c ? [c] : [];
  });
  const [minDisc, setMinDisc] = useState(0);
  const [sort, setSort] = useState("distance");
  const [filterOpen, setFilterOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [q, setQ] = useState("");
  const [stores, setStores] = useState(null);
  const [searching, setSearching] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [inviteBiz, setInviteBiz] = useState(null);
  const [expandedMacro, setExpandedMacro] = useState(null);
  const [noResultsOpen, setNoResultsOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [inviteStoreSearchOpen, setInviteStoreSearchOpen] = useState(false);
  const [trending, setTrending] = useState([]);
  const [claims, setClaims] = useState([]);
  const [hideClaimed, setHideClaimed] = useState(() => localStorage.getItem("kn_hide_claimed") === "1");

  const fetchClaimed = useCallback(async () => {
    if (!getToken("client")) { setClaims([]); return; }
    try {
      const r = await api.get("/client/claims", { headers: authHeaders("client") });
      setClaims(r.data.claims || []);
    } catch (_) {}
  }, []);
  useEffect(() => { fetchClaimed(); }, [fetchClaimed, client]);
  useEffect(() => {
    const onChanged = () => fetchClaimed();
    window.addEventListener("kn-coupons-changed", onChanged);
    return () => window.removeEventListener("kn-coupons-changed", onChanged);
  }, [fetchClaimed]);
  // Map an offer to the client's claim only when it belongs to the offer's CURRENT cycle,
  // so a coupon used in a previous cycle no longer marks a relaunched offer as claimed.
  const claimedMap = React.useMemo(() => {
    const cyc = new Map(offers.map((o) => [o.offer_id, o.cycle || 1]));
    const m = new Map();
    claims.forEach((c) => {
      if (!c.offer_id) return;
      const oc = cyc.has(c.offer_id) ? cyc.get(c.offer_id) : (c.cycle || 1);
      if ((c.cycle || 1) === oc) m.set(c.offer_id, c.claim_id);
    });
    return m;
  }, [offers, claims]);
  const markClaimed = useCallback((offerId, cid) =>
    setClaims((s) => [...s, { offer_id: offerId, claim_id: cid, cycle: (offers.find((o) => o.offer_id === offerId)?.cycle) || 1 }]),
  [offers]);
  const toggleHideClaimed = () => setHideClaimed((v) => { const nv = !v; localStorage.setItem("kn_hide_claimed", nv ? "1" : "0"); return nv; });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loc.lat == null || loc.lng == null) return;
    api.get("/categories/trending", { params: { lat: loc.lat, lng: loc.lng, radius: loc.radius || 25 } })
      .then((r) => setTrending(r.data.trending || [])).catch(() => {});
  }, [loc.lat, loc.lng, loc.radius]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { lat: loc.lat, lng: loc.lng, radius: loc.radius };
      if (selCats.length) params.categories = selCats.join(",");
      if (minDisc) params.min_discount = minDisc;
      if (sort === "rating") params.sort = "rating";
      const r = await api.get("/offers/today", { params });
      setOffers(r.data);
    } catch (_) {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [loc.lat, loc.lng, loc.radius, selCats, minDisc, sort]);

  useEffect(() => { load(); }, [load]);

  const runSearch = async (term) => {
    if (!term || term.trim().length < 2) { setStores(null); setDiscovered([]); return; }
    setSearching(true);
    let storesCount = 0;
    try {
      const r = await api.get("/stores/search", { params: { q: term.trim(), lat: loc.lat, lng: loc.lng } });
      setStores(r.data.stores || []);
      storesCount = (r.data.stores || []).length;
    } catch (_) { setStores([]); }
    finally { setSearching(false); }
    // Also look for businesses NOT yet on KoupoNow (Google), to invite them.
    let discoveredCount = 0;
    try {
      const d = await api.get("/stores/discover", { params: { q: term.trim(), lat: loc.lat, lng: loc.lng } });
      setDiscovered(d.data.stores || []);
      discoveredCount = (d.data.stores || []).length;
    } catch (_) { setDiscovered([]); }
    if (term.trim().length >= 3 && storesCount === 0 && discoveredCount === 0) setNoResultsOpen(true);
  };

  const toggleCat = (id) =>
    setSelCats((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const savedIds = new Set(client?.following ? [] : []);
  const savedSet = new Set((client && client.saved_offer_ids) || []);

  const toggleSave = async (offerId) => {
    if (!getToken("client")) { navigate("/coupons"); return; }
    try {
      await api.post(`/client/saved/${offerId}`, {}, { headers: authHeaders("client") });
      refreshClient();
    } catch (_) {}
  };

  const macroCats = cats; // [{id,label,icon,tags:[...]}]

  return (
    <div className="space-y-4">
      <InstallAppBanner />
      {client && <SavingsHero />}
      {client && <ProfileSuggestionBanner client={client} onDone={refreshClient} />}
      {/* Hero + search */}
      <div className="kn-fade-up">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          Le migliori offerte <span className="text-[#E63946]">vicino a te</span>
        </h1>
        <p className="text-slate-500 text-sm mt-1">Scopri, salva e risparmia ogni giorno.</p>

        <div className="mt-3 flex items-center gap-2">
          <button
            data-testid="location-button"
            onClick={openLocation}
            className="flex items-center gap-1.5 px-3.5 h-11 rounded-2xl bg-white border border-slate-200 hover:border-[#E63946] transition-colors text-sm font-semibold text-slate-700 min-w-0 flex-1"
          >
            <MapPin className="h-4 w-4 text-[#E63946] shrink-0" />
            <span className="truncate">{loc.label} · {loc.radius}km</span>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-auto" />
          </button>
          <button
            data-testid="open-map-btn"
            onClick={() => navigate("/map")}
            className="shrink-0 h-11 px-4 rounded-2xl bg-slate-900 text-white font-bold text-sm flex items-center gap-2 hover:bg-slate-800 transition-colors"
          >
            <MapIcon className="h-5 w-5" /> Mappa
          </button>
        </div>

        {/* Category chips (macro) — subito sotto la posizione */}
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
          {macroCats.map((m) => {
            const activeInMacro = m.tags.some((t) => selCats.includes(t.id));
            const isOpen = expandedMacro === m.id;
            return (
              <button
                key={m.id}
                data-testid={`macro-cat-${m.id}`}
                onClick={() => setExpandedMacro(isOpen ? null : m.id)}
                className={`shrink-0 px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  activeInMacro ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-white border border-slate-200 text-slate-600"
                }`}
              >
                <CatIcon id={m.id} className="h-4 w-4" />
                {m.label}
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            );
          })}
        </div>

        {/* Subcategory chips for the expanded macro */}
        {expandedMacro && (() => {
          const m = macroCats.find((x) => x.id === expandedMacro);
          if (!m) return null;
          return (
            <div className="mt-2 flex flex-wrap gap-2 kn-fade-up" data-testid={`subcats-${m.id}`}>
              {m.tags.map((t) => (
                <button key={t.id} data-testid={`subcat-${t.id}`} onClick={() => toggleCat(t.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${selCats.includes(t.id) ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-slate-100 text-slate-600"}`}>
                  <CatIcon id={t.id} className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              ))}
            </div>
          );
        })()}

        <div className="mt-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              data-testid="store-search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
              placeholder="Cerca un negozio..."
              className="w-full h-12 pl-11 pr-9 rounded-2xl bg-white border border-slate-200 text-[15px] outline-none focus:border-[#E63946] transition-colors"
            />
            {q && (
              <button onClick={() => { setQ(""); setStores(null); setDiscovered([]); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            )}
          </div>
          <button
            data-testid="open-filters"
            onClick={() => setFilterOpen(true)}
            className="h-12 w-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center relative"
          >
            <SlidersHorizontal className="h-5 w-5" />
            {(selCats.length > 0 || minDisc > 0) && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full kn-gradient text-[10px] flex items-center justify-center font-bold">
                {selCats.length + (minDisc ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        <button data-testid="search-alertme" onClick={() => setAlertOpen(true)}
          className="mt-2 text-left text-sm text-[#B91C3B] font-semibold flex items-start gap-1.5 hover:underline">
          <BellPlus className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="underline">Avvisami quando c'è l'offerta che sto cercando</span>
        </button>
        <button data-testid="search-no-results" onClick={() => setNoResultsOpen(true)}
          className="mt-2 text-left text-sm text-[#B91C3B] font-semibold flex items-start gap-1.5 hover:underline">
          <Rocket className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="underline">Invita amici e negozi e sarà più facile risparmiare!</span>
        </button>
      </div>

      {/* Store search results */}
      {stores !== null && (
        <div className="kn-card p-3 space-y-1" data-testid="store-search-results">
          <p className="text-xs font-bold text-slate-400 px-1 pb-1 uppercase tracking-wide">Negozi trovati</p>
          {searching && <div className="p-3 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca...</div>}
          {!searching && stores.length === 0 && <p className="p-3 text-slate-400 text-sm">Nessun negozio trovato.</p>}
          {stores.map((s) => (
            <button
              key={s.vendor_id}
              data-testid={`store-result-${s.vendor_id}`}
              onClick={() => navigate(`/store/${s.vendor_id}`)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 text-left"
            >
              <div className="h-11 w-11 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                {s.logo ? <img src={s.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 truncate flex items-center gap-1">
                  {s.store_name}
                  {s.verified && <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" title="Negozio verificato" />}
                </p>
                <p className="text-xs text-slate-400 truncate">{s.category_label}{s.city ? ` · ${s.city}` : ""}{s.distance_km != null ? ` · ${distanceLabel(s.distance_km)}` : ""}</p>
              </div>
              <span className="text-xs font-bold text-[#E63946] shrink-0">{s.active_count} offerte</span>
            </button>
          ))}
        </div>
      )}

      {/* Not yet on KoupoNow -> invite */}
      {stores !== null && discovered.length > 0 && (
        <div className="kn-card p-3 space-y-1" data-testid="discover-results">
          <p className="text-xs font-bold text-slate-400 px-1 pb-1 uppercase tracking-wide flex items-center gap-1.5">
            <UserPlus className="h-3.5 w-3.5" /> Non ancora su KoupoNow
          </p>
          {discovered.map((s) => (
            <div key={s.place_id} className="w-full flex items-center gap-3 p-2 rounded-xl">
              <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Store className="h-5 w-5 text-slate-400" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                <p className="text-xs text-slate-400 truncate">{s.address}</p>
              </div>
              <button onClick={() => setInviteBiz(s)} data-testid={`invite-${s.place_id}`}
                className="shrink-0 h-9 px-3 rounded-xl bg-slate-900 text-white font-bold text-sm flex items-center gap-1.5 hover:bg-slate-800">
                <UserPlus className="h-4 w-4" /> Chiedi coupon!
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Sort + Category chips */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        <span className="text-xs font-bold text-slate-400 shrink-0">Ordina:</span>
        <button data-testid="sort-distance" onClick={() => setSort("distance")}
          className={`shrink-0 px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${sort === "distance" ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-white border border-slate-200 text-slate-600"}`}>
          Più vicini
        </button>
        <button data-testid="sort-rating" onClick={() => setSort("rating")}
          className={`shrink-0 px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${sort === "rating" ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-white border border-slate-200 text-slate-600"}`}>
          Meglio recensiti
        </button>
      </div>

      {/* Category chips (macro) */}

      {/* Trending categories near the client */}
      {trending.length > 0 && !expandedMacro && (
        <div className="space-y-1.5" data-testid="trending-row">
          <p className="text-xs font-bold text-slate-400 px-1 flex items-center gap-1"><Rocket className="h-3.5 w-3.5 text-[#E63946]" /> In tendenza vicino a te</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
            {trending.map((t) => (
              <button key={t.id} data-testid={`trending-${t.id}`} onClick={() => { setSelCats([t.id]); setExpandedMacro(null); }}
                className={`shrink-0 px-3 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${selCats.includes(t.id) ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-[#FFF1F2] text-[#B91C3B]"}`}>
                <CatIcon id={t.id} className="h-4 w-4" />
                {t.label}
                <span className="text-[11px] font-bold opacity-70">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Offers grid */}
      {!loading && offers.length > 0 && client && claimedMap.size > 0 && (
        <label className="flex items-center justify-between gap-3 kn-card px-4 py-2.5" data-testid="hide-claimed-toggle">
          <span className="text-sm font-bold text-slate-600">Nascondi offerte già scaricate</span>
          <button
            onClick={toggleHideClaimed}
            role="switch"
            aria-checked={hideClaimed}
            data-testid="hide-claimed-switch"
            className={`relative h-6 w-11 rounded-full transition-colors ${hideClaimed ? "bg-[#E63946]" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${hideClaimed ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </label>
      )}
      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="kn-card overflow-hidden">
              <Skeleton className="h-28 w-full" />
              <div className="p-2.5 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
            </div>
          ))}
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 kn-fade-up">
          <div className="h-20 w-20 rounded-3xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="h-9 w-9 text-[#E63946]" />
          </div>
          <p className="font-bold text-slate-800 text-lg">Nessuna offerta qui</p>
          <p className="text-slate-500 text-sm mt-1">Prova ad ampliare il raggio o cambiare zona.</p>
        </div>
      ) : (() => {
        const visibleOffers = hideClaimed ? offers.filter((o) => !claimedMap.has(o.offer_id)) : offers;
        if (visibleOffers.length === 0) {
          return (
            <div className="text-center py-16 kn-fade-up" data-testid="all-claimed-empty">
              <div className="h-20 w-20 rounded-3xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="h-9 w-9 text-emerald-500" />
              </div>
              <p className="font-bold text-slate-800 text-lg">Hai già scaricato tutto!</p>
              <p className="text-slate-500 text-sm mt-1">Disattiva "Nascondi già scaricate" per rivederle.</p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-2 gap-3 auto-rows-fr">
            {visibleOffers.map((o) => (
              <div key={o.offer_id} className="kn-fade-up h-full">
                <OfferCard offer={o} saved={savedSet.has(o.offer_id)} onToggleSave={toggleSave} onNeedAuth={() => setAuthOpen(true)} claimed={claimedMap.has(o.offer_id)} claimId={claimedMap.get(o.offer_id)} onClaimed={markClaimed} />
              </div>
            ))}
          </div>
        );
      })()}

      {/* Grow-together invite banner — in fondo pagina, sfondo neutro, testo centrato */}
      <div className="rounded-2xl px-4 py-5 bg-white border border-slate-200 kn-shadow-soft flex flex-col items-center text-center gap-3" data-testid="grow-banner">
        <p className="font-extrabold text-base flex items-center gap-2 text-slate-900"><Rocket className="h-5 w-5 text-[#E63946]" /> Più siamo, più risparmiamo</p>
        <p className="text-sm text-slate-500 max-w-md">Aiutaci a far crescere KoupoNow: più amici e negozi partecipano, più offerte e risparmi per tutti.</p>
        <div className="flex gap-2 flex-wrap justify-center">
          <button data-testid="grow-invite-friends" onClick={() => shareWA(`Ho scoperto KoupoNow: offerte e coupon dai negozi vicino a te! Unisciti anche tu, più siamo più risparmiamo 👉 ${SITE_URL}`)}
            className="h-10 px-4 rounded-xl bg-[#FFE066] hover:bg-[#FFD84A] text-slate-900 font-bold text-sm flex items-center justify-center gap-1.5 transition-colors"><Users className="h-4 w-4" /> Invita amici</button>
          <button data-testid="grow-invite-store" onClick={() => setInviteStoreSearchOpen(true)}
            className="h-10 px-4 rounded-xl bg-white border border-slate-300 text-slate-800 font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-slate-50 transition-colors"><Store className="h-4 w-4" /> Invita un negozio</button>
        </div>
        <button data-testid="open-no-results" onClick={() => setNoResultsOpen(true)}
          className="text-xs font-bold text-slate-500 underline underline-offset-2 hover:text-[#E63946] transition-colors mt-1">
          Non trovi quello che cerchi?
        </button>
      </div>

      {/* Filters sheet */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-w-3xl mx-auto max-h-[85vh] overflow-y-auto" data-testid="filters-sheet">
          <SheetHeader className="text-left"><SheetTitle className="text-xl font-extrabold">Filtri</SheetTitle></SheetHeader>
          <div className="py-4 space-y-6">
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">Sconto minimo</p>
              <div className="flex gap-2">
                {DISCOUNTS.map((d) => (
                  <button key={d.v} data-testid={`disc-${d.v}`} onClick={() => setMinDisc(d.v)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold ${minDisc === d.v ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-slate-100 text-slate-600"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            {macroCats.map((m) => (
              <div key={m.id}>
                <p className="text-sm font-bold text-slate-700 mb-2">{m.label}</p>
                <div className="flex flex-wrap gap-2">
                  {m.tags.map((t) => (
                    <button key={t.id} data-testid={`cat-${t.id}`} onClick={() => toggleCat(t.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold ${selCats.includes(t.id) ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-slate-100 text-slate-600"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 sticky bottom-0 bg-white pt-2 pb-1">
            <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={() => { setSelCats([]); setMinDisc(0); }}>Azzera</Button>
            <Button className="flex-1 h-12 rounded-xl font-bold" data-testid="apply-filters" onClick={() => setFilterOpen(false)}>Mostra offerte</Button>
          </div>
        </SheetContent>
      </Sheet>

      <InviteSheet open={!!inviteBiz} onOpenChange={(v) => !v && setInviteBiz(null)} business={inviteBiz} />
      <InviteStoreSearchDialog
        open={inviteStoreSearchOpen}
        onOpenChange={setInviteStoreSearchOpen}
        position={loc}
        onPick={(biz) => setInviteBiz(biz)}
      />
      <ClientAuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setAuthOpen(false)} />
      <AlertMeSheet open={alertOpen} onOpenChange={setAlertOpen} macros={macroCats} onNeedAuth={() => setAuthOpen(true)} />

      <Dialog open={noResultsOpen} onOpenChange={setNoResultsOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl text-center" data-testid="no-results-dialog">
          <div className="h-16 w-16 rounded-3xl bg-[#FFE4E6] flex items-center justify-center mx-auto"><Rocket className="h-8 w-8 text-[#E63946]" /></div>
          <p className="text-xl font-extrabold text-slate-900 mt-2">Non l'abbiamo ancora trovato!</p>
          <p className="text-sm text-slate-500">KoupoNow sta crescendo ogni giorno. Aiutaci a diventare grandi insieme: invita i tuoi amici e i negozi che ami. Più siamo, più offerte e risparmi per tutti! 🚀</p>
          <div className="flex flex-col gap-2 mt-2">
            <Button data-testid="noresult-invite-friends" onClick={() => shareWA(`Ho scoperto KoupoNow: offerte e coupon dai negozi vicino a te! Unisciti, più siamo più risparmiamo 👉 ${SITE_URL}`)} className="w-full h-11 rounded-xl font-bold gap-2"><Users className="h-4 w-4" /> Invita amici</Button>
            <Button variant="outline" data-testid="noresult-invite-store" onClick={() => { setNoResultsOpen(false); setInviteStoreSearchOpen(true); }} className="w-full h-11 rounded-xl font-bold gap-2"><Store className="h-4 w-4" /> Invita un negozio</Button>
            <Button variant="ghost" data-testid="noresult-alertme" onClick={() => { setNoResultsOpen(false); setAlertOpen(true); }} className="w-full h-11 rounded-xl font-bold gap-2 text-[#E63946]"><BellPlus className="h-4 w-4" /> Avvisami quando arriva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
